// Sprint F19 — Extrato bancário (OFX): ingestão + classificação manual.
// Entry slim após refactor topológico — modais e tipos em ./financeiro-extrato/.
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Card, Button, Input, Select, Tag, Modal, Typography, Space,
  Alert, Table, App as AntApp, Flex, Empty, DatePicker,
} from 'antd';
import {
  UploadOutlined, LinkOutlined, SearchOutlined, LeftOutlined, RightOutlined,
  DisconnectOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { SwapOutlined } from '@ant-design/icons';
import { ModalTransferencias } from './financeiro-extrato/ModalTransferencias';
import { API_URL } from '../config';
import { fmtDataBr } from '../utils/dataFmt';
import {
  PRESETS_PERIODO, presetMesAtual, detectarModoNavegador, rotuloNavegador,
  navegarPeriodo,
} from '../utils/periodo';

import type { Transacao, ContaDisponivel } from './financeiro-extrato/types';
import { BANCO_LABEL, CLASSIF_AUTO_CONCILIA } from './financeiro-extrato/types';
import { getToken, fmt, isConciliada } from './financeiro-extrato/utils';
import ModalCriarLancamento from './financeiro-extrato/ModalCriarLancamento';
import ModalConciliar from './financeiro-extrato/ModalConciliar';
import ModalUploadOFX from './financeiro-extrato/ModalUploadOFX';
import ModalClassificar from './financeiro-extrato/ModalClassificar';

const { Title, Text } = Typography;

function FinanceiroExtratoBancarioInner() {
  const { message, modal } = AntApp.useApp();
  const [searchParams] = useSearchParams();
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [contas, setContas] = useState<ContaDisponivel[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [periodoIni, setPeriodoIni] = useState<string>(() => presetMesAtual().ini);
  const [periodoFim, setPeriodoFim] = useState<string>(() => presetMesAtual().fim);
  const [presetAtivo, setPresetAtivo] = useState<string>('Este mês');
  const [pickerCustomAberto, setPickerCustomAberto] = useState(false);  // range picker do navegador
  const [filtroBanco, setFiltroBanco] = useState<string>('');
  const [filtroConta, setFiltroConta] = useState<string>('');
  // filtrosClassif: mantido pra retrocompat e regras automaticas — nao exposto na UI
  const [filtrosClassif] = useState<string[]>([]);
  // Filtro de status de conciliação: 'todas' | 'nao_conciliadas' | 'conciliadas'
  const [filtroConciliacao, setFiltroConciliacao] = useState<'todas' | 'nao_conciliadas' | 'conciliadas'>('todas');
  // Filtro entrada/saida — substitui o filtro de classificacao OFX
  const [filtroSinal, setFiltroSinal] = useState<'todas' | 'entrada' | 'saida'>('todas');
  // Stats GLOBAIS (independente do periodo filtrado) — meta de conciliação
  // dividida em pre-cut (historico CA, informacional) e pos-cut (obrigatorio).
  const [statsGlobal, setStatsGlobal] = useState<{
    data_cut_conciliacao: string;
    total: number; vinculadas: number; auto_classif: number; cobertas: number;
    cobertura_pct: number; pendentes: number; pendentes_despesa: number;
    pendentes_receita: number; soma_pendente: number;
    pos: { total: number; cobertas: number; pendentes: number;
      cobertura_pct: number; soma_pendente: number; };
    pre: { total: number; cobertas: number; cobertura_pct: number; };
  } | null>(null);
  const [busca, setBusca] = useState('');
  // Contexto vindo de Contas a Receber: "buscar saldo dessa parcela"
  const [contextoBusca, setContextoBusca] = useState<{
    cliente: string; valor_alvo: number; parcela_id: string;
  } | null>(null);
  // Onda 0 (2026-07-02): contexto chega pela URL (?q, ?status, ?conta, ?valor,
  // ?parcela) — link copiável e refresh-safe; substitui o handoff frágil via
  // sessionStorage (ofx_busca_pendente). Quem escreve é o ModalParcela do CR.
  useEffect(() => {
    try {
      const q = searchParams.get('q');
      const status = searchParams.get('status');
      const conta = searchParams.get('conta');
      const valorRaw = searchParams.get('valor');
      const parcelaId = searchParams.get('parcela');
      if (q) {
        // Usa só primeira palavra significativa do cliente (>=4 chars)
        const primeira = q.split(/\s+/).find((w: string) => w.length >= 4) || q;
        setBusca(primeira);
      }
      if (status === 'nao_conciliadas' || status === 'conciliadas' || status === 'todas') {
        setFiltroConciliacao(status);
      }
      if (conta) setFiltroConta(conta);
      const valorAlvo = valorRaw !== null ? parseFloat(valorRaw) : NaN;
      if (parcelaId && Number.isFinite(valorAlvo)) {
        setContextoBusca({ cliente: q || '', valor_alvo: valorAlvo, parcela_id: parcelaId });
      }
    } catch { /* URL sem contexto — fluxo normal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [mostrarUpload, setMostrarUpload] = useState(false);
  const [mostrarTransf, setMostrarTransf] = useState(false);
  const [classificando, setClassificando] = useState<Transacao | null>(null);
  const [conciliando, setConciliando] = useState<Transacao | null>(null);
  const [autoConciliando, setAutoConciliando] = useState(false);
  const [mostrarEscolhaAuto, setMostrarEscolhaAuto] = useState(false);
  const [criandoLancamento, setCriandoLancamento] = useState<Transacao | null>(null);
  // F-OFX-bulk: seleção pra ações em lote
  const [selecionadas, setSelecionadas] = useState<React.Key[]>([]);
  const [aplicandoLote, setAplicandoLote] = useState(false);

  async function carregar() {
    setLoading(true); setErro('');
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (periodoIni) params.set('data_ini', periodoIni);
      if (periodoFim) params.set('data_fim', periodoFim);
      if (filtroBanco) params.set('banco', filtroBanco);
      if (filtroConta) params.set('conta_id', filtroConta);
      const [r, rStats] = await Promise.all([
        axios.get(`${API_URL}/financeiro/transacoes-bancarias?${params}`, { headers: { Authorization: token } }),
        axios.get(`${API_URL}/financeiro/transacoes-bancarias/stats`, { headers: { Authorization: token } }),
      ]);
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setTransacoes(Array.isArray(d.transacoes) ? d.transacoes : []);
      setContas(Array.isArray(d.contas_disponiveis) ? d.contas_disponiveis : []);
      const ds = rStats.data.body ? JSON.parse(rStats.data.body) : rStats.data;
      setStatsGlobal(ds);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ },
    [periodoIni, periodoFim, filtroBanco, filtroConta]);

  function aplicarPreset(p: { ini: string; fim: string; rotulo: string }) {
    setPeriodoIni(p.ini); setPeriodoFim(p.fim); setPresetAtivo(p.rotulo);
  }
  function _navegar(delta: number) {
    const n = navegarPeriodo(periodoIni, periodoFim, delta);
    setPeriodoIni(n.ini); setPeriodoFim(n.fim); setPresetAtivo('');
  }

  const filtradas = useMemo(() => transacoes.filter(t => {
    if (filtrosClassif.length > 0 && !filtrosClassif.includes(t.classificacao)) return false;
    if (filtroConciliacao !== 'todas') {
      const conciliada = isConciliada(t);
      if (filtroConciliacao === 'nao_conciliadas' && conciliada) return false;
      if (filtroConciliacao === 'conciliadas' && !conciliada) return false;
    }
    if (filtroSinal !== 'todas') {
      const v = parseFloat(String(t.valor)) || 0;
      if (filtroSinal === 'entrada' && v <= 0) return false;
      if (filtroSinal === 'saida' && v >= 0) return false;
    }
    if (busca) {
      const q = busca.toLowerCase();
      if (!t.descricao.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [transacoes, filtrosClassif, filtroConciliacao, filtroSinal, busca]);

  // Contadores pra mostrar nos chips do filtro de conciliação
  const contadoresConciliacao = useMemo(() => {
    let conc = 0, nconc = 0;
    for (const t of transacoes) {
      if (isConciliada(t)) conc++;
      else nconc++;
    }
    return { conc, nconc, total: transacoes.length };
  }, [transacoes]);

  async function executarAutoConciliacao(escopo: 'periodo' | 'pos_cut' | 'tudo') {
    setAutoConciliando(true);
    setMostrarEscolhaAuto(false);
    try {
      const token = await getToken();
      const body: any = { limit: 2000 };
      if (escopo === 'periodo') {
        body.data_ini = periodoIni;
        body.data_fim = periodoFim;
        if (filtroConta) body.conta_id = filtroConta;
      } else if (escopo === 'pos_cut' && statsGlobal?.data_cut_conciliacao) {
        body.data_ini = statsGlobal.data_cut_conciliacao;
      }
      // escopo === 'tudo' → sem filtros (inclui pré-cut)
      const r = await axios.post(`${API_URL}/financeiro/transacoes-bancarias/auto-conciliar`,
        body, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.info(
        `${d.sugeridas ?? 0} com sugestão forte · ${d.ambiguas} ambíguas · ${d.sem_candidatos} sem candidato · `
        + `${d.total_processadas} avaliadas. Nada foi vinculado — abra a transação para confirmar.`,
        10
      );
      carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAutoConciliando(false); }
  }

  function rodarAutoConciliacao() {
    if (autoConciliando) return;
    setMostrarEscolhaAuto(true);
  }

  // F-OFX-bulk: ações em lote sobre as selecionadas
  async function autoConciliarLote() {
    if (!selecionadas.length) return;
    setAplicandoLote(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/transacoes-bancarias/auto-conciliar`,
        { ids: selecionadas as string[] },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.info(
        `${d.sugeridas ?? 0} com sugestão forte · ${d.ambiguas} ambíguas · ${d.sem_candidatos} sem candidato. `
        + 'Nada foi vinculado — abra a transação para confirmar.', 10);
      setSelecionadas([]);
      carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAplicandoLote(false); }
  }

  function desconciliarLote() {
    if (!selecionadas.length) return;
    modal.confirm({
      title: `Desconciliar ${selecionadas.length} transações?`,
      content: 'Os vínculos com parcelas/lançamentos serão removidos. As parcelas/lançamentos NÃO serão revertidos — só perdem a ligação com estas transações.',
      okText: 'Desconciliar', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        setAplicandoLote(true);
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/financeiro/transacoes-bancarias/bulk-desconciliar`,
            { ids: selecionadas as string[] },
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          message.success(`${d.desconciliadas} desconciliadas.`);
          setSelecionadas([]);
          carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        } finally { setAplicandoLote(false); }
      },
    });
  }

  function desconciliar(t: Transacao) {
    modal.confirm({
      title: 'Remover o vínculo?',
      content: 'A parcela/lançamento NÃO é revertida — só perde a ligação com esta transação.',
      okText: 'Desconciliar', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/financeiro/transacoes-bancarias/${t.id}/desconciliar`,
            {}, { headers: { Authorization: token } });
          message.success('Desconciliada.');
          carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  async function aplicarClassificacao(transacao: Transacao, novaClassif: string, criarRegra?: { contem_texto: string; sinal_valor: string }) {
    try {
      const token = await getToken();
      const payload: any = { classificacao: novaClassif };
      if (criarRegra) payload.criar_regra = criarRegra;
      const r = await axios.patch(`${API_URL}/financeiro/transacoes-bancarias/${transacao.id}`,
        payload, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setTransacoes(prev => prev.map(x => x.id === transacao.id ? d.transacao : x));
      if (d.regra?.aplicada_em > 0) {
        message.success(`Regra criada e aplicada em ${d.regra.aplicada_em} outras transações.`);
        carregar();
      } else {
        message.success('Classificação salva.');
      }
      setClassificando(null);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // FZ-TRANSF fase 2 — casa a transação com a transferência do Conta Azul
  // sugerida (Inter→Sicoob etc.) em 1 clique. Vínculo de 1 lado, contábil neutro.
  async function conciliarTransferenciaCA(t: Transacao) {
    if (!t.transf_ca_sugestao) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/financeiro/transacoes-bancarias/${t.id}/conciliar`,
        { tipo: 'transferencia_ca', alvo_id: t.transf_ca_sugestao.id },
        { headers: { Authorization: token } });
      message.success('Transferência conciliada.');
      carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  const modoNav = detectarModoNavegador(periodoIni, periodoFim);

  const cols: ColumnsType<Transacao> = [
    {
      title: 'Data', dataIndex: 'data_transacao', key: 'd', width: 100,
      render: (v) => <Text strong style={{ fontSize: 11 }}>{fmtDataBr(v)}</Text>,
    },
    {
      title: 'Descrição', dataIndex: 'descricao', key: 'desc',
      // ellipsis evita que descrições PIX gigantes empurrem a tabela pra fora.
      // Tooltip via title HTML expõe o texto completo no hover.
      ellipsis: { showTitle: false },
      render: (_, t) => (
        <div title={t.descricao} style={{ minWidth: 0 }}>
          <Text style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.descricao}
          </Text>
          <Text type="secondary" style={{ fontSize: 9, fontFamily: 'monospace' }}>
            {t.tipo_ofx} · FITID {t.fitid.slice(-12)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Valor', dataIndex: 'valor', key: 'v', align: 'right', width: 110,
      render: (v) => (
        <Text strong style={{ color: v > 0 ? '#0a7f4a' : '#B91626', fontSize: 12 }}>
          {v > 0 ? '+' : ''}{fmt(v)}
        </Text>
      ),
    },
    {
      title: 'Conta', dataIndex: 'banco', key: 'b', width: 130,
      render: (v, t) => (
        <div>
          <Text style={{ fontSize: 11 }}>{BANCO_LABEL[v] || v}</Text>
          <div><Text type="secondary" style={{ fontSize: 9 }}>{t.conta_id}</Text></div>
        </div>
      ),
    },
    // Coluna "Categoria"/Classificação removida — a categoria DRE real vem do
    // CR/CP vinculado (visível no modal de detalhe). Foco do extrato e conciliar.
    {
      title: 'Vínculo', key: 'vinc', align: 'center', width: 180, fixed: 'right',
      render: (_, t) => {
        const conciliada = !!(t.parcela_id || t.lancamento_pagar_id || t.par_transacao_id || t.transferencia_ca_id);
        const autoClassif = !conciliada && CLASSIF_AUTO_CONCILIA.has(t.classificacao);
        const vinculoLabel = t.parcela_label || t.lancamento_pagar_label
          || (t.par_conta_label ? `↔ ${t.par_conta_label}` : null)
          || (t.transferencia_ca_label ? `🔄 ${t.transferencia_ca_label}` : null);
        if (conciliada) {
          return (
            <Space direction="vertical" size={2} style={{ alignItems: 'center', width: '100%' }}>
              <Tag color="success" title={vinculoLabel || ''}
                style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>
                ✓ {vinculoLabel ? vinculoLabel.slice(0, 22) : 'conciliada'}
              </Tag>
              <Button type="link" size="small" style={{ fontSize: 10, padding: 0, height: 'auto' }} onClick={() => desconciliar(t)}>
                ✕ desconciliar
              </Button>
            </Space>
          );
        }
        if (autoClassif) {
          // Auto-conciliada via classificacao — nao precisa CR/CP. Mostra
          // ✓ sem botao desconciliar (pra "desconciliar" muda a classificacao
          // de volta pra nao_classificada).
          return (
            <Tag color="success" style={{ margin: 0, fontSize: 11 }}>
              ✓ via classificação
            </Tag>
          );
        }
        // FZ-TRANSF fase 2 — sugestão de transferência do CA (Inter→Sicoob etc.):
        // casa em 1 clique, mostrando o destino do outro lado.
        if (t.transf_ca_sugestao) {
          return (
            <Space direction="vertical" size={2} style={{ alignItems: 'center', width: '100%' }}>
              <Tag color="blue" title={`${t.transf_ca_sugestao.origem} → ${t.transf_ca_sugestao.destino}`}
                style={{ margin: 0, fontSize: 10, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                🔄 transf. → {String(t.transf_ca_sugestao.outro || '').slice(0, 16)}
              </Tag>
              <Button size="small" type="primary" ghost onClick={() => conciliarTransferenciaCA(t)} style={{ fontSize: 11 }}>
                Vincular transferência
              </Button>
              <Button type="link" size="small" style={{ fontSize: 10, padding: 0, height: 'auto' }} onClick={() => setConciliando(t)}>
                outras opções
              </Button>
            </Space>
          );
        }
        return (
          <Button size="small" type="primary" ghost icon={<LinkOutlined />} onClick={() => setConciliando(t)}>
            Conciliar
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '16px 20px' }}>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={14} style={{ marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>💳 Extrato bancário</Title>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Transações importadas de OFX. Classifique manualmente — o sistema aprende.
          </Text>
        </div>
        <Space>
          <Button type="primary" icon={<LinkOutlined />} loading={autoConciliando} onClick={rodarAutoConciliacao}
            title="Mostra quantas transações têm um candidato provável (valor + data + nome). Não vincula nada.">
            Sugerir vínculos
          </Button>
          <Button icon={<SwapOutlined />} onClick={() => setMostrarTransf(true)}
            title="Lançar transferência entre contas (PIX, aplicação, resgate) — a conciliação casa com o extrato depois">
            Transferências
          </Button>
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setMostrarUpload(true)}
            style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
            Importar OFX
          </Button>
        </Space>
      </Flex>

      {/* Banner de contexto vindo de Contas a Receber */}
      {contextoBusca && (
        <Card size="small" style={{ marginBottom: 12, background: '#EFF3FF', borderColor: '#B7C5E8' }}>
          <Flex justify="space-between" align="center" gap={10} wrap="wrap">
            <div>
              <Text strong style={{ fontSize: 12, color: '#0930D0', display: 'block' }}>
                🔍 Buscando saldo da parcela do Contas a Receber
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Cliente: <strong>{contextoBusca.cliente}</strong> ·
                Saldo a conciliar: <strong>{fmt(contextoBusca.valor_alvo)}</strong> ·
                Filtros aplicados: busca + não conciliadas
              </Text>
            </div>
            <Button size="small" onClick={() => { setContextoBusca(null); setBusca(''); setFiltroConciliacao('todas'); }}>
              ✕ Limpar contexto
            </Button>
          </Flex>
        </Card>
      )}

      {/* Banner de Cobertura — dividido em POS-CUT (meta operacional) e
          PRE-CUT (historico informacional do CA). data_cut_conciliacao vem
          da config admin.empresa_dados. */}
      {statsGlobal && (() => {
        const dataCut = statsGlobal.data_cut_conciliacao;
        const dataCutBr = dataCut ? new Date(dataCut + 'T12:00:00').toLocaleDateString('pt-BR') : '?';
        const pos = statsGlobal.pos;
        const pre = statsGlobal.pre;
        const posVazio = pos.total === 0;
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 14 }}>
            {/* CARD POS-CUT — meta principal */}
            <Card size="small" style={{
              background: posVazio ? '#FAFBFF'
                       : pos.cobertura_pct >= 99 ? '#E8F5EE'
                       : pos.cobertura_pct >= 80 ? '#FFFCF5' : '#FDECEC',
              borderColor: posVazio ? '#D8DCEC'
                       : pos.cobertura_pct >= 99 ? '#0a7f4a'
                       : pos.cobertura_pct >= 80 ? '#B07500' : '#B91626',
            }}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={10}>
                <div>
                  <Text style={{ fontSize: 11, fontWeight: 600, color: '#666' }}>
                    🎯 META: 100% conciliado pós-cut <span style={{ color: '#999' }}>(a partir de {dataCutBr})</span>
                  </Text>
                  {posVazio ? (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Nenhuma transação pós-cut ainda — o sistema começa a popular a partir de {dataCutBr}.
                      </Text>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 2 }}>
                        <Text strong style={{
                          fontSize: 28,
                          color: pos.cobertura_pct >= 99 ? '#0a7f4a'
                               : pos.cobertura_pct >= 80 ? '#B07500' : '#B91626',
                        }}>
                          {pos.cobertura_pct.toFixed(1)}%
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {pos.cobertas} de {pos.total} transações
                        </Text>
                      </div>
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        ⏳ {pos.pendentes} pendente{pos.pendentes !== 1 ? 's' : ''} ·{' '}
                        R$ {pos.soma_pendente.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                    </>
                  )}
                </div>
              </Flex>
            </Card>

            {/* CARD PRE-CUT — informacional (historico CA) */}
            <Card size="small" style={{ background: '#FAFBFF', borderColor: '#D8DCEC' }}>
              <div>
                <Text style={{ fontSize: 11, fontWeight: 600, color: '#666' }}>
                  📚 Histórico pré-cut <span style={{ color: '#999' }}>(antes de {dataCutBr})</span>
                </Text>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 2 }}>
                  <Text strong style={{ fontSize: 22, color: '#666' }}>
                    {pre.cobertura_pct.toFixed(1)}%
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {pre.cobertas}/{pre.total}
                  </Text>
                </div>
                <Text type="secondary" style={{ fontSize: 10, fontStyle: 'italic' }}>
                  Conciliação opcional — fonte da verdade é o CA migrado.
                </Text>
              </div>
            </Card>
          </div>
        );
      })()}

      {/* KPIs do periodo removidos — categoria DRE real vem do CR/CP vinculado.
          Quem quer ver totalizadores por categoria abre o DRE ou Fluxo de Caixa.
          Aqui o foco e conciliar OFX com CR/CP. */}

      <Card size="small" style={{ marginBottom: 14 }} bodyStyle={{ padding: 12 }}>
        <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 10 }}>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Space.Compact>
              <Button size="small" icon={<LeftOutlined />} title="Período anterior" onClick={() => _navegar(-1)} />
              <Button size="small" onClick={() => setPickerCustomAberto(true)}
                title="Clique para escolher um intervalo personalizado"
                style={{ minWidth: 150, textTransform: 'capitalize' }}>
                {rotuloNavegador(periodoIni, periodoFim)}
                <Text type="secondary" style={{ fontSize: 9, marginLeft: 4 }}>({modoNav})</Text>
              </Button>
              <Button size="small" icon={<RightOutlined />} title="Próximo período" onClick={() => _navegar(1)} />
            </Space.Compact>
            <DatePicker.RangePicker
              open={pickerCustomAberto} onOpenChange={setPickerCustomAberto}
              value={periodoIni && periodoFim ? [dayjs(periodoIni), dayjs(periodoFim)] : null}
              format="DD/MM/YYYY" allowClear={false}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setPeriodoIni(dates[0].format('YYYY-MM-DD'));
                  setPeriodoFim(dates[1].format('YYYY-MM-DD'));
                  setPresetAtivo('');
                }
                setPickerCustomAberto(false);
              }}
              style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
                opacity: 0, pointerEvents: 'none' }} />
          </span>
          {PRESETS_PERIODO.map(({ k, fn }) => (
            <Tag.CheckableTag key={k} checked={presetAtivo === k} onChange={() => aplicarPreset(fn())}>
              {k}
            </Tag.CheckableTag>
          ))}
        </Flex>
        <Flex gap={10} align="center" wrap="wrap" style={{ marginBottom: 10 }}>
          <Select value={filtroBanco || undefined} onChange={(v) => { setFiltroBanco(v || ''); setFiltroConta(''); }}
            placeholder="Todos bancos" allowClear size="small" style={{ width: 140 }}
            options={Array.from(new Set(contas.map(c => c.banco))).map(b => ({ label: BANCO_LABEL[b] || b, value: b }))} />
          <Select value={filtroConta || undefined} onChange={(v) => setFiltroConta(v || '')}
            placeholder="Todas contas" allowClear size="small" style={{ minWidth: 220, flex: 1 }}
            options={contas.filter(c => !filtroBanco || c.banco === filtroBanco)
              .map(c => ({ label: c.conta_label, value: c.conta_id }))} />
          <Input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar na descrição…"
            prefix={<SearchOutlined style={{ color: '#aaa' }} />} allowClear size="small"
            style={{ flex: 1, minWidth: 240 }} />
        </Flex>
        <Flex gap={6} wrap="wrap" align="center" style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>
            Conciliação <span style={{ color: '#bbb', fontWeight: 400 }}>(do período)</span>:
          </Text>
          <Tag.CheckableTag
            checked={filtroConciliacao === 'todas'}
            onChange={() => setFiltroConciliacao('todas')}
          >
            Todas ({contadoresConciliacao.total})
          </Tag.CheckableTag>
          <Tag.CheckableTag
            checked={filtroConciliacao === 'nao_conciliadas'}
            onChange={() => setFiltroConciliacao('nao_conciliadas')}
            style={filtroConciliacao === 'nao_conciliadas'
              ? { background: '#FFF4E0', color: '#B07500', borderColor: '#FFE0A8' } : undefined}
          >
            🔗 Não conciliadas ({contadoresConciliacao.nconc})
          </Tag.CheckableTag>
          <Tag.CheckableTag
            checked={filtroConciliacao === 'conciliadas'}
            onChange={() => setFiltroConciliacao('conciliadas')}
            style={filtroConciliacao === 'conciliadas'
              ? { background: '#E8F5EE', color: '#0a7f4a', borderColor: '#B7E0C6' } : undefined}
          >
            ✓ Conciliadas ({contadoresConciliacao.conc})
          </Tag.CheckableTag>
        </Flex>
        {/* Filtro Entrada/Saida — substitui filtro de classificacao */}
        <Flex gap={6} wrap="wrap" align="center">
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>
            Sinal <span style={{ color: '#bbb', fontWeight: 400 }}>(do período)</span>:
          </Text>
          <Tag.CheckableTag
            checked={filtroSinal === 'todas'}
            onChange={() => setFiltroSinal('todas')}
          >
            Todas
          </Tag.CheckableTag>
          <Tag.CheckableTag
            checked={filtroSinal === 'entrada'}
            onChange={() => setFiltroSinal('entrada')}
            style={filtroSinal === 'entrada'
              ? { background: '#E8F5EE', color: '#0a7f4a', borderColor: '#B7E0C6' } : undefined}
          >
            ⬆ Entradas
          </Tag.CheckableTag>
          <Tag.CheckableTag
            checked={filtroSinal === 'saida'}
            onChange={() => setFiltroSinal('saida')}
            style={filtroSinal === 'saida'
              ? { background: '#FDECEC', color: '#B91626', borderColor: '#FFCCC7' } : undefined}
          >
            ⬇ Saídas
          </Tag.CheckableTag>
        </Flex>
      </Card>

      {erro && <Alert type="error" message={erro} showIcon closable onClose={() => setErro('')} style={{ marginBottom: 14 }} />}

      {/* F-OFX-bulk: barra de ações em lote (aparece quando há seleção) */}
      {selecionadas.length > 0 && (
        <div style={{
          padding: '10px 14px', background: '#E8EEFF', borderRadius: 6,
          marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <Text strong style={{ color: '#020D7F', fontSize: 12 }}>
            {selecionadas.length} transação{selecionadas.length !== 1 ? 'ões' : ''} selecionada{selecionadas.length !== 1 ? 's' : ''}
          </Text>
          <Space wrap>
            {/* Só SUGERE — vincular é sempre ato humano (decisão 2026-07-30,
                depois de o matcher casar um PIX de R$ 40 com uma remuneração
                de R$ 4.500 que nem tinha vencido). */}
            <Button type="primary" icon={<LinkOutlined />}
              loading={aplicandoLote} onClick={autoConciliarLote}
              title="Mostra quais lançamentos o sistema considera prováveis (score ≥ 180). Não vincula nada — quem vincula é você, abrindo a transação.">
              Sugerir vínculos
            </Button>
            <Button icon={<DisconnectOutlined />} loading={aplicandoLote} onClick={desconciliarLote} danger>
              Desconciliar
            </Button>
            <Button onClick={() => setSelecionadas([])}>Limpar seleção</Button>
          </Space>
        </div>
      )}

      <Table
        rowKey="id" columns={cols} dataSource={filtradas} loading={loading}
        size="small" pagination={{ pageSize: 100, showSizeChanger: true }}
        rowClassName={(r) => {
          // Linha pre-cut: visual sutilmente acinzentado (historico CA, nao obrigatorio)
          const isPreCut = statsGlobal?.data_cut_conciliacao
            && r.data_transacao && r.data_transacao < statsGlobal.data_cut_conciliacao;
          if (isPreCut) return 'row-pre-cut';
          return r.classificacao === 'nao_classificada' ? 'row-pendente' : '';
        }}
        locale={{ emptyText: <Empty description={transacoes.length === 0 ? 'Nenhuma transação. Importe um OFX pra começar.' : 'Nenhuma transação com os filtros atuais.'} /> }}
        scroll={{ x: 1050 }}
        rowSelection={{
          selectedRowKeys: selecionadas,
          onChange: (keys) => setSelecionadas(keys),
          preserveSelectedRowKeys: true,
        }}
      />

      {mostrarUpload && <ModalUploadOFX onFechar={() => setMostrarUpload(false)} onSucesso={() => { setMostrarUpload(false); carregar(); }} />}

      {/* Modal escolha do escopo da sugestão */}
      <Modal open={mostrarEscolhaAuto} onCancel={() => setMostrarEscolhaAuto(false)}
        footer={null} title="Sugerir vínculos" width={540}>
        <Text style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          Conta quantas transações têm um candidato <strong>provável</strong> (score ≥ 180 — valor,
          data e nome batendo). <strong>Nada é vinculado</strong>: quem confirma é você, abrindo a transação.
        </Text>
        <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 11 }}
          message="Por que não vincula sozinho"
          description="O matcher já errou casando um PIX de R$ 40,00 com uma remuneração de R$ 4.500 do mesmo favorecido, que nem tinha vencido. Vínculo errado dobra dinheiro no fluxo de caixa e é difícil de achar depois." />
        <Space direction="vertical" style={{ width: '100%' }}>
          <Card size="small" hoverable
            onClick={() => executarAutoConciliacao('periodo')}
            style={{ cursor: 'pointer', borderColor: '#0930D0' }}>
            <Flex justify="space-between" align="center" gap={10}>
              <div>
                <Text strong style={{ fontSize: 13, color: '#0930D0' }}>📅 Só o período visível</Text>
                <div><Text type="secondary" style={{ fontSize: 11 }}>
                  Mais conservador — afeta só o que tá filtrado na tela ({fmtDataBr(periodoIni)} a {fmtDataBr(periodoFim)})
                </Text></div>
              </div>
              <Button type="primary" ghost>Rodar</Button>
            </Flex>
          </Card>
          {/* RECOMENDADO: só pós-cut — escopo operacional do sistema */}
          <Card size="small" hoverable
            onClick={() => executarAutoConciliacao('pos_cut')}
            style={{ cursor: 'pointer', borderColor: '#0a7f4a', borderWidth: 1.5 }}>
            <Flex justify="space-between" align="center" gap={10}>
              <div>
                <Text strong style={{ fontSize: 13, color: '#0a7f4a' }}>
                  ✓ Só pós-cut (recomendado)
                </Text>
                <div><Text type="secondary" style={{ fontSize: 11 }}>
                  A partir de {statsGlobal?.data_cut_conciliacao
                    ? new Date(statsGlobal.data_cut_conciliacao + 'T12:00:00').toLocaleDateString('pt-BR')
                    : '?'}
                  {' '}— {statsGlobal?.pos.pendentes ?? '?'} pendentes (R$ {(statsGlobal?.pos.soma_pendente ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).
                  Pré-cut é histórico CA, não precisa conciliar.
                </Text></div>
              </div>
              <Button type="primary" style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
                Rodar pós-cut
              </Button>
            </Flex>
          </Card>
          <Card size="small" hoverable
            onClick={() => executarAutoConciliacao('tudo')}
            style={{ cursor: 'pointer', borderColor: '#B91626' }}>
            <Flex justify="space-between" align="center" gap={10}>
              <div>
                <Text strong style={{ fontSize: 13, color: '#B91626' }}>🚀 TODAS (inclui pré-cut histórico)</Text>
                <div><Text type="secondary" style={{ fontSize: 11 }}>
                  {statsGlobal?.pendentes ?? '?'} pendentes globais. Útil pra testes/auditoria — mas pré-cut já está consolidado no CA.
                </Text></div>
              </div>
              <Button type="primary" danger>Rodar TUDO</Button>
            </Flex>
          </Card>
        </Space>
      </Modal>
      {classificando && <ModalClassificar transacao={classificando} onFechar={() => setClassificando(null)} onSalvar={aplicarClassificacao} />}
      {conciliando && (
        <ModalConciliar transacao={conciliando}
          onFechar={() => setConciliando(null)}
          onConciliou={() => { setConciliando(null); carregar(); }}
          onCriarLancamento={(t) => { setConciliando(null); setCriandoLancamento(t); }} />
      )}
      {criandoLancamento && (
        <ModalCriarLancamento transacao={criandoLancamento}
          onFechar={() => setCriandoLancamento(null)}
          onCriou={() => { setCriandoLancamento(null); carregar(); }} />
      )}
      <ModalTransferencias aberto={mostrarTransf}
        onFechar={() => { setMostrarTransf(false); carregar(); }} />
    </div>
  );
}

export default function FinanceiroExtratoBancario() {
  return (
    <AntApp>
      <FinanceiroExtratoBancarioInner />
    </AntApp>
  );
}
